/**
 * TanStack Query Client Setup
 * Provides query client configuration for data fetching
 */

import { QueryClient } from '@tanstack/react-query';

import { useWorkspaceStore } from '@/store/useWorkspaceStore';

/**
 * Create and configure the QueryClient instance
 *
 * Configuration:
 * - defaultOptions: Global defaults for all queries
 *   - staleTime: 30 seconds - data is considered fresh for 30s
 *   - gcTime: 5 minutes - cached data kept for 5min
 *   - retry: 1 - retry failed requests once
 *   - refetchOnWindowFocus: false - don't refetch on window focus
 *   - throwOnError: function - throw errors to error boundaries unless workspace was cleared
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
      // Throw errors to error boundaries so they can be caught by TanStack Router.
      // Exception: when the RPC 403 handler has already cleared the workspace
      // (access revoked), suppress the throw so the workspace resolver can
      // redirect instead of showing the generic error boundary.
      throwOnError: () => {
        if (!useWorkspaceStore.getState().selectedWorkspaceId) return false;
        return true;
      },
    },
    mutations: {
      retry: 1,
      // Don't throw mutation errors - they're handled with toast notifications
      throwOnError: false,
    },
  },
});

